import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    try {
      const { data } = await api.get("/auth/google/url");
      window.location.href = data.url;
    } catch (e) {
      setErr("Could not start Google sign-in");
    }
  };

  const forgotPassword = async () => {
    try {
      const { data } = await api.get("/auth/google/url", { params: { forgot_password: true } });
      // Stash the "forgot" intent so the callback page knows to open the reset dialog.
      window.sessionStorage.setItem("gw_forgot_password", "1");
      window.location.href = data.url;
    } catch (e) {
      setErr("Could not start password reset");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 70% 60%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center font-bold font-display text-xl">S</div>
            <div>
              <div className="font-display font-bold text-2xl">Sourcd</div>
              <div className="text-xs tracking-[0.25em] uppercase text-indigo-200 mt-0.5">By GetWork</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight mb-4">
              Find the right candidate.<br /><span className="text-indigo-200">In under 30 seconds.</span>
            </h1>
            <p className="text-indigo-100 max-w-md text-base leading-relaxed">
              The internal candidate database for GetWork recruiters. Sync your Google Sheet, search across 50,000+ candidates, and never lose a follow-up.
            </p>
          </div>
          <div className="text-indigo-200 text-xs">© 2026 GetWork. India&apos;s leading fresher hiring platform.</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white font-bold font-display">S</div>
            <div>
              <div className="font-display font-bold">Sourcd</div>
              <div className="text-[10px] tracking-[0.2em] font-semibold text-indigo-600 uppercase">By GetWork</div>
            </div>
          </div>

          <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">Welcome back</h2>
          <p className="text-slate-500 mb-8">Sign in with your GetWork email to continue.</p>

          <Button
            data-testid="google-signin-button"
            variant="outline"
            onClick={googleLogin}
            className="w-full mb-5 h-11 border-slate-300 hover:bg-slate-50"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400 uppercase tracking-wider">or</span></div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input
                data-testid="login-email-input"
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@getwork.org"
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={forgotPassword}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                data-testid="login-password-input"
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 mt-1.5"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{err}</div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit" disabled={busy}
              className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-slate-400 mt-8 text-center">
            Access is restricted to approved GetWork email addresses. <br />
            Need access? Contact your Super Custodian.
          </p>
        </div>
      </div>
    </div>
  );
}
